import React, { useState } from 'react'
import { Navigate, Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Grid,
  VStack,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Button,
  Text,
  Alert,
  AlertIcon,
  Link,
  HStack,
  Image,
  useBreakpointValue,
} from '@chakra-ui/react'
import { keyframes } from '@emotion/react'
import { useAuth } from '@/contexts/AuthContext'
import { useAppTranslation } from '@/contexts/LanguageContext'
import CoreTechLogo from '@/assets/logo-white.svg'
import CoreTechLogoBlue from '@/assets/logo.svg'

// Animation keyframes for geometric patterns
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(180deg); }
`

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.1); }
`

const drift = keyframes`
  0% { transform: translateX(-100px) translateY(0px); }
  50% { transform: translateX(100px) translateY(-50px); }
  100% { transform: translateX(-100px) translateY(0px); }
`

const AnimatedBackground = () => (
  <Box position="relative" w="full" h="full" overflow="hidden">
    {/* Gradient Background */}
    <Box
      position="absolute"
      top={0}
      left={0}
      w="full"
      h="full"
      // bgGradient="linear(135deg, blue.400 0%, blue.500 50%, blue.600 100%)"
      bgGradient="linear(135deg, blue.500 0%, blue.700 50%, blue.800 100%)"
    />

    {/* Animated Geometric Patterns */}
    <Box position="absolute" top="10%" left="10%" w="60px" h="60px">
      <Box
        w="full"
        h="full"
        bg="whiteAlpha.200"
        borderRadius="lg"
        sx={{
          animation: `${float} 6s ease-in-out infinite`,
          animationDelay: '0s',
        }}
      />
    </Box>

    <Box position="absolute" top="30%" right="15%" w="80px" h="80px">
      <Box
        w="full"
        h="full"
        bg="whiteAlpha.150"
        borderRadius="50%"
        sx={{
          animation: `${pulse} 4s ease-in-out infinite`,
          animationDelay: '1s',
        }}
      />
    </Box>

    <Box position="absolute" bottom="20%" left="20%" w="40px" h="40px">
      <Box
        w="full"
        h="full"
        bg="whiteAlpha.300"
        transform="rotate(45deg)"
        sx={{
          animation: `${float} 8s ease-in-out infinite`,
          animationDelay: '2s',
        }}
      />
    </Box>

    <Box position="absolute" top="60%" left="60%" w="100px" h="100px">
      <Box
        w="full"
        h="full"
        bg="whiteAlpha.100"
        borderRadius="lg"
        sx={{
          animation: `${drift} 12s linear infinite`,
          animationDelay: '0.5s',
        }}
      />
    </Box>

    <Box position="absolute" bottom="40%" right="25%" w="50px" h="50px">
      <Box
        w="full"
        h="full"
        bg="whiteAlpha.250"
        borderRadius="md"
        sx={{
          clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
          animation: `${pulse} 5s ease-in-out infinite`,
          animationDelay: '3s',
        }}
      />
    </Box>

    {/* Particle System - Small floating dots */}
    {Array.from({ length: 15 }).map((_, i) => (
      <Box
        key={i}
        position="absolute"
        w="4px"
        h="4px"
        bg="whiteAlpha.300"
        borderRadius="50%"
        top={`${Math.random() * 100}%`}
        left={`${Math.random() * 100}%`}
        sx={{
          animation: `${float} ${4 + Math.random() * 4}s ease-in-out infinite`,
          animationDelay: `${Math.random() * 2}s`,
        }}
      />
    ))}

    {/* Larger geometric elements */}
    <Box position="absolute" top="15%" left="70%" w="120px" h="120px">
      <Box
        w="full"
        h="full"
        bg="whiteAlpha.100"
        borderRadius="50%"
        sx={{
          animation: `${pulse} 7s ease-in-out infinite`,
          animationDelay: '1.5s',
        }}
      />
    </Box>

    <Box position="absolute" bottom="30%" left="5%" w="90px" h="90px">
      <Box
        w="full"
        h="full"
        bg="whiteAlpha.150"
        borderRadius="md"
        transform="rotate(30deg)"
        sx={{
          animation: `${float} 9s ease-in-out infinite`,
          animationDelay: '2.5s',
        }}
      />
    </Box>
  </Box>
)

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const { login, isAuthenticated } = useAuth()
  const { t } = useAppTranslation(['auth', 'common'])
  const headingText = useBreakpointValue({
    base: 'Document Hub',
    lg: t('login.title', { ns: 'auth' }),
  })

  // Only redirect if authenticated AND not in error/loading state
  if (isAuthenticated && !isLoading && !error) {
    return <Navigate to="/dashboard" replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      await login(email, password)
      // Navigation will happen automatically via AuthContext
    } catch (err: any) {
      setError(err.response?.data?.error || t('login.loginError', { ns: 'auth' }))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} h="100vh">
      {/* Left Column - Animated Background (hidden on medium and below) */}
      <Box position="relative" display={{ base: 'none', lg: 'block' }}>
        <AnimatedBackground />

        {/* Content overlay on animated background */}
        <VStack
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          spacing={6}
          color="white"
          textAlign="center"
          zIndex={10}
        >
          <Image src={CoreTechLogo} alt="Coretech" h="100px" />
          <Heading size="2xl" fontWeight="bold">
            Document Hub
          </Heading>
          <Text fontSize="lg" maxW="400px" opacity={0.9}>
            {t('welcomeMessage', { ns: 'dashboard' })}
          </Text>
        </VStack>
      </Box>

      {/* Right Column - Login Form */}
      <Box bg="white" display="flex" alignItems="center" justifyContent="center" p={8}>
        <Box w="full" maxW="400px">
          <VStack spacing={8}>
            <VStack spacing={3} textAlign="center">
              {/* Blue logo for medium devices and below */}
              <Image
                src={CoreTechLogoBlue}
                alt="Coretech"
                h="80px"
                display={{ base: 'block', lg: 'none' }}
              />
              <Heading size="xl" color="gray.800">
                {headingText}
              </Heading>
              <Text color="gray.600" fontSize="md">
                {t('login.subtitle', { ns: 'auth' })}
              </Text>
            </VStack>

            {error && (
              <Alert status="error" borderRadius="lg">
                <AlertIcon />
                {error}
              </Alert>
            )}

            <Box as="form" onSubmit={handleSubmit} w="full">
              <VStack spacing={6}>
                <FormControl isRequired>
                  <FormLabel color="gray.700" fontWeight="medium">
                    {t('login.emailLabel', { ns: 'auth' })}
                  </FormLabel>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('login.emailLabel', { ns: 'auth' })}
                    size="lg"
                    borderRadius="lg"
                    borderColor="gray.200"
                    _hover={{ borderColor: 'blue.300' }}
                    _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color="gray.700" fontWeight="medium">
                    {t('login.passwordLabel', { ns: 'auth' })}
                  </FormLabel>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('login.passwordLabel', { ns: 'auth' })}
                    size="lg"
                    borderRadius="lg"
                    borderColor="gray.200"
                    _hover={{ borderColor: 'blue.300' }}
                    _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                  />
                </FormControl>

                <Button
                  type="submit"
                  w="full"
                  size="lg"
                  colorScheme="blue"
                  isLoading={isLoading}
                  loadingText={t('login.signingIn', { ns: 'auth' })}
                  borderRadius="lg"
                  fontWeight="semibold"
                  _hover={{ transform: 'translateY(-1px)', boxShadow: 'lg' }}
                  transition="all 0.2s"
                >
                  {t('login.loginButton', { ns: 'auth' })}
                </Button>

                <HStack justify="center" w="full">
                  <Link
                    as={RouterLink}
                    to="/forgot-password"
                    fontSize="sm"
                    color="blue.500"
                    _hover={{ color: 'blue.600', textDecoration: 'underline' }}
                  >
                    {t('login.forgotPassword', { ns: 'auth' })}
                  </Link>
                </HStack>
              </VStack>
            </Box>
          </VStack>
        </Box>
      </Box>
    </Grid>
  )
}
